/**
 * Custom hook for handling image uploads with Jodit Pro
 * Provides clean state management, error handling, and retry logic
 */

import { useState, useCallback } from 'react';
import { apiPostFormData } from '../utils/apiAuth';

export const useImageUpload = () => {
  const [uploadState, setUploadState] = useState({
    isUploading: false,
    progress: 0,
    error: null,
    success: false,
    uploadedImages: []
  });

  const resetUploadState = useCallback(() => {
    setUploadState({
      isUploading: false,
      progress: 0,
      error: null,
      success: false,
      uploadedImages: []
    });
  }, []);

  const uploadImage = useCallback(async (file, subtopicName, retryCount = 0) => {
    const maxRetries = 3;
    
    try {
      setUploadState(prev => ({
        ...prev,
        isUploading: true,
        progress: 0,
        error: null,
        success: false
      }));

      // Validate file
      if (!file) {
        throw new Error('No file provided');
      }

      if (!subtopicName) {
        throw new Error('Subtopic name is required');
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error(`File type ${file.type} not allowed. Allowed types: ${allowedTypes.join(', ')}`);
      }

      // Validate file size (5MB max)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        throw new Error(`File size exceeds maximum allowed size of 5MB`);
      }

      // Create form data
      const formData = new FormData();
      formData.append('file', file);
      formData.append('subtopic_name', subtopicName);

      // Simulate progress
      setUploadState(prev => ({ ...prev, progress: 25 }));

      // Upload image
      const response = await apiPostFormData('/content_creation/upload-image/', formData, {
        timeout: 30000, // 30 seconds timeout
      });

      setUploadState(prev => ({ ...prev, progress: 75 }));

      // Check response
      if (!response.data.success) {
        throw new Error(response.data.message || 'Upload failed');
      }

      setUploadState(prev => ({ ...prev, progress: 100 }));

      // Success
      const uploadedImage = {
        url: response.data.files[0].url,
        name: response.data.files[0].name,
        size: response.data.files[0].size,
        uploadedAt: new Date().toISOString()
      };

      setUploadState(prev => ({
        ...prev,
        isUploading: false,
        success: true,
        uploadedImages: [...prev.uploadedImages, uploadedImage]
      }));

      return uploadedImage;

    } catch (error) {
      
      // Retry logic
      if (retryCount < maxRetries && error.code !== 'VALIDATION_ERROR') {
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
        return uploadImage(file, subtopicName, retryCount + 1);
      }

      // Final error
      const errorMessage = error.response?.data?.detail || error.message || 'Upload failed';
      
      setUploadState(prev => ({
        ...prev,
        isUploading: false,
        error: errorMessage,
        success: false
      }));

      throw new Error(errorMessage);
    }
  }, []);

  const clearError = useCallback(() => {
    setUploadState(prev => ({ ...prev, error: null }));
  }, []);

  const clearSuccess = useCallback(() => {
    setUploadState(prev => ({ ...prev, success: false }));
  }, []);

  return {
    uploadState,
    uploadImage,
    resetUploadState,
    clearError,
    clearSuccess
  };
};

